import { FunctionComponent, ReactElement, useEffect, useState } from "react";

import { Icon, Typography, styled } from "@mui/material";

import { LoadingButton } from "@mui/lab";

import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";

import { Paper, SaveAlert, WorkspaceToolbar } from "../../components/common";
import {
    useCreateFreebieRule,
    useGetFreebieRuleById,
    useUpdateFreebieRule,
} from "../../hooks";
import { useToggleCallbacks } from "../../sdk/hooks";
import { FreebieRuleStatus, FreebieRuleType } from "../../utils/enums";

import {
    CheckpointSection,
    DiscountCodeSection,
    IFreebieRuleForm,
    TypeSection,
    VariantsSection,
} from "./sections";

const Root = styled("main")`
    padding: ${({ theme }) => theme.spacing(4, 0)};

    display: flex;
    gap: ${({ theme }) => theme.spacing(2)};

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
      flex-direction: column;
      gap: ${theme.spacing(3)};
    }
  `}
`;

const LeftContainer = styled("section")`
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};

    max-width: 800px;
    width: 100%;

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
      max-width: 100%;
    }
  `}
`;

const RightContainer = styled("section")`
    min-width: 350px;
    max-width: 500px;
    padding-top: ${({ theme }) => theme.spacing(6)};

    flex: 0.5;

    ${({ theme }) => `
    ${theme.breakpoints.down("md")} {
          padding-top: 0px;
          min-width: 100%;
          max-width: 100%;
    }
  `}
`;

const SummaryContainer = styled("section")`
    position: sticky;
    top: 95px;

    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing(2)};
`;

const Title = styled(Typography)`
    font-size: 22px;
    font-weight: 600;
`;

const SectionTitle = styled(Typography)`
    font-size: 18px;
    font-weight: 500;
    color: #626262;
`;

const List = styled("ul")`
    list-style-type: disc;
    padding: 0 0px 0px 18px;
    margin: 0;
`;

const ListItem = styled("li")`
    font-family: "Poppins";
    padding: 8px 0px;
`;

const Container = styled("div")`
    width: 100%;
    padding: ${({ theme }) => theme.spacing(0, 6)};

    ${({ theme }) => theme.breakpoints.down("md")} {
        padding: ${({ theme }) => theme.spacing(0, 3)};
    }
`;

const validationSchema = Yup.object({
    name: Yup.string()
        .min(1)
        .required("Please enter a valid name for this rule"),
    type: Yup.number()
        .oneOf([
            FreebieRuleType.ELIGIBILITY,
            FreebieRuleType.CHECKPOINT,
            FreebieRuleType.DISCOUNT_CODE,
        ])
        .required("Please select a rule type"),
    status: Yup.number()
        .oneOf([
            FreebieRuleStatus.ACTIVE,
            FreebieRuleStatus.INACTIVE,
            FreebieRuleStatus.ARCHIVED,
        ])
        .required("Please select a status for this rule"),
    quantity: Yup.number()
        .min(1, "Quantity must be at least 1")
        .required("Please enter a quantity"),
    priority: Yup.number()
        .min(0, "Priority cannot be negative")
        .required("Please enter a priority"),
    minCartValue: Yup.number().when("type", (value, field) => {
        const type = value[0];
        return type === FreebieRuleType.CHECKPOINT
            ? field
                  .min(1, "Minimum cart value must be at least 1")
                  .required("Please enter a minimum cart value")
            : field.nullable();
    }),
    discountCode: Yup.string().when("type", (value, field) => {
        const type = value[0];
        return type === FreebieRuleType.DISCOUNT_CODE
            ? field.required("Please enter a discount code")
            : field.nullable();
    }),
    triggerVariantIds: Yup.array()
        .of(Yup.string())
        .when("type", (value, field) => {
            const type = value[0];
            return type === FreebieRuleType.ELIGIBILITY
                ? field.min(1, "Please select at least one trigger variant")
                : field;
        }),
    freebieVariantIds: Yup.array()
        .of(Yup.string())
        .min(1, "Please select at least one freebie variant"),
});

export const FreebieRuleScreen: FunctionComponent = (): ReactElement => {
    const params = useParams();
    const newFreebieRule = params.id === "new";
    const navigate = useNavigate();

    const freebieRule = useGetFreebieRuleById("freebieRule", params.id);
    const createMutation = useCreateFreebieRule("freebieRule");
    const updateMutation = useUpdateFreebieRule("freebieRule");

    const [alertOpen, setAlertOpen] = useState(false);

    const [handleAlertOpen, handleAlertClose] =
        useToggleCallbacks(setAlertOpen);

    const {
        values,
        handleChange,
        setFieldValue,
        handleSubmit,
        errors,
        touched,
        handleBlur,
        dirty,
        isValid,
    } = useFormik<IFreebieRuleForm>({
        initialValues: {
            name: "",
            type: FreebieRuleType.ELIGIBILITY,
            status: FreebieRuleStatus.ACTIVE,
            minCartValue: "",
            discountCode: "",
            quantity: 1,
            priority: 0,
            triggerVariantIds: [],
            freebieVariantIds: [],
        },
        onSubmit: async (values) => {
            const {
                minCartValue,
                discountCode,
                quantity,
                priority,
                type,
                status,
                ...rest
            } = values;

            const payload = {
                ...rest,
                type: Number(type),
                status: Number(status),
                quantity: Number(quantity),
                priority: Number(priority),
                minCartValue:
                    minCartValue === "" ? null : Number(minCartValue),
                discountCode: discountCode === "" ? null : discountCode,
                startsAt: null,
                endsAt: null,
                triggerVariantIds:
                    Number(type) === FreebieRuleType.ELIGIBILITY
                        ? values.triggerVariantIds
                        : [],
            };

            if (newFreebieRule) {
                await createMutation.mutateAsync(payload, {
                    onSuccess: () => {
                        navigate("/freebie-rules");
                    },
                });
            } else {
                await updateMutation.mutateAsync(
                    {
                        ...payload,
                        id: params.id ?? "",
                    },
                    {
                        onSuccess: () => {
                            navigate("/freebie-rules");
                        },
                    },
                );
            }
        },
        validationSchema: validationSchema,
    });

    useEffect(() => {
        if (Boolean(freebieRule.data) && freebieRule.isSuccess) {
            setFieldValue("name", freebieRule.data.name);
            setFieldValue("type", freebieRule.data.type);
            setFieldValue("status", freebieRule.data.status);
            setFieldValue(
                "minCartValue",
                freebieRule.data.minCartValue !== null
                    ? freebieRule.data.minCartValue
                    : "",
            );
            setFieldValue(
                "discountCode",
                freebieRule.data.discountCode ?? "",
            );
            setFieldValue("quantity", freebieRule.data.quantity);
            setFieldValue("priority", freebieRule.data.priority);
            setFieldValue(
                "triggerVariantIds",
                freebieRule.data.triggerVariantIds,
            );
            setFieldValue(
                "freebieVariantIds",
                freebieRule.data.freebieVariantIds,
            );
        }
    }, [freebieRule.data, freebieRule.isSuccess, setFieldValue]);

    return (
        <>
            <WorkspaceToolbar
                title={newFreebieRule ? "Create freebie rule" : "Edit freebie rule"}
                enableSearch={false}
                actions={
                    <LoadingButton
                        variant="contained"
                        color="primary"
                        size="small"
                        endIcon={<Icon>check_circle</Icon>}
                        onClick={handleAlertOpen}
                        disabled={!dirty || !isValid}
                        loading={
                            createMutation.isLoading || updateMutation.isLoading
                        }
                    >
                        Save
                    </LoadingButton>
                }
                breadcrumbs={[
                    {
                        title: "Freebie Rules",
                        link: "/freebie-rules",
                        icon: <Icon fontSize="small">card_giftcard</Icon>,
                    },
                    {
                        title: newFreebieRule
                            ? "Create freebie rule"
                            : freebieRule.data?.name ?? "",
                        link: `/freebie-rules/${params.id}`,
                    },
                ]}
            />
            <SaveAlert
                open={alertOpen}
                onCancel={handleAlertClose}
                onSave={handleSubmit}
            />
            <Container>
                <Root>
                    <LeftContainer>
                        <Title>Configure freebie rule</Title>
                        <TypeSection
                            values={values}
                            errors={errors}
                            touched={touched}
                            onBlur={handleBlur}
                            onChange={handleChange}
                        />
                        {Number(values.type) === FreebieRuleType.CHECKPOINT && (
                            <CheckpointSection
                                values={values}
                                errors={errors}
                                touched={touched}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )}
                        {Number(values.type) ===
                            FreebieRuleType.DISCOUNT_CODE && (
                            <DiscountCodeSection
                                values={values}
                                errors={errors}
                                touched={touched}
                                onBlur={handleBlur}
                                onChange={handleChange}
                            />
                        )}
                        <VariantsSection
                            values={values}
                            setFieldValue={setFieldValue}
                        />
                    </LeftContainer>
                    <RightContainer>
                        <SummaryContainer>
                            <Paper>
                                <SectionTitle>Summary</SectionTitle>
                                <List>
                                    <ListItem>
                                        {values.freebieVariantIds.length}{" "}
                                        freebie variant(s), quantity{" "}
                                        {values.quantity || 1}
                                    </ListItem>
                                    {Number(values.type) ===
                                        FreebieRuleType.ELIGIBILITY && (
                                        <ListItem>
                                            Triggered by{" "}
                                            {values.triggerVariantIds.length}{" "}
                                            product(s) in cart
                                        </ListItem>
                                    )}
                                    {Number(values.type) ===
                                        FreebieRuleType.CHECKPOINT && (
                                        <ListItem>
                                            Triggered at cart value ≥ ₹
                                            {values.minCartValue || 0}
                                            {values.discountCode
                                                ? ` or discount code "${values.discountCode}"`
                                                : ""}
                                        </ListItem>
                                    )}
                                    {Number(values.type) ===
                                        FreebieRuleType.DISCOUNT_CODE && (
                                        <ListItem>
                                            Triggered by discount code "
                                            {values.discountCode || "—"}"
                                        </ListItem>
                                    )}
                                </List>
                            </Paper>
                        </SummaryContainer>
                    </RightContainer>
                </Root>
            </Container>
        </>
    );
};
